

import React, { useEffect, useState } from "react";

/**
 * AvatarPicture component generates a circular avatar image with initials or station ID.
 * The avatar is rendered as a data URL from a canvas element with a colored background.
 * @memberof module:components/avatars
 * @param {object} props - Component props.
 * @param {number} [props.width=40] - Width of the avatar image.
 * @param {number} [props.height=40] - Height of the avatar image.
 * @param {string} [props.first_name] - First name for initials.
 * @param {string} [props.last_name] - Last name for initials.
 * @param {string} [props.email_address] - Email address fallback for initials.
 * @param {string} [props.stationId] - Station ID to use as initials.
 * @param {string} [props.stationName] - Station name for tooltip.
 * @param {boolean} [props.showTooltip=false] - Whether to show tooltip on hover.
 *
 * @returns {JSX.Element} Avatar image element.
 */
const AvatarPicture = ({
  width =40,
  height=40,
  first_name,
  last_name,
  email_address,
  stationId,
  stationName,
  showTooltip = false,
}) => {
   /** 
   * Number of letters in initials used to calculate font size dynamically.
   * @type {number}
   */
  const [lettersCount, setLettersCount] = useState(0);

  /**
   * Initials or text to display in the avatar  
   * @type {string}*/
  const [initials, setInitials] = useState("");

  /**
   * Tooltip text to show on hover 
   *  @type {string}*/
  const [tooltip, setTooltip] = useState("");

  /** 
   * Background color of the avatar in hex format
   * @type {string} */
  const [color, setColor] = useState("");

  const textPadding = 3;

  /**
   * Convert HSL color values to HEX string.
   *
   * @param {number} h - Hue (0-360).
   * @param {number} s - Saturation percentage (0-100).
   * @param {number} l - Lightness percentage (0-100).
   * @returns {string} Hex color string (e.g., "#aabbcc").
   */
  function hslToHex(h, s, l) {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;

    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };

    return `#${f(0)}${f(8)}${f(4)}`;
  }

 /**
   * Generate a random HEX color based on given HSL parameters.
   *
   * @param {object} params - Parameters for color generation.
   * @param {number} [params.hue=0] - Base hue.
   * @param {number} [params.hueRange=360] - Range for random hue offset.
   * @param {number} [params.brightness=50] - Lightness percentage.
   * @param {number} [params.saturation=80] - Saturation percentage.
   * @returns {string} Randomly generated HEX color string.
   */
  function generateColor({
    hue = 0,
    hueRange = 360,
    brightness = 50,
    saturation = 80,
  }) {
    const h = (hue + Math.floor(Math.random() * hueRange)) % 360;
    const s = saturation; // % saturation
    const l = brightness; // % brightness (lightness)

    return hslToHex(h, s, l);
  }

  // Effect to update initials and tooltip text based on props
  useEffect(() => {
    if (first_name && last_name) {
      setInitials(`${first_name[0]}${last_name[0]}`);
      setTooltip(`${first_name} ${last_name}`);
      setLettersCount(2.5);
    } else if (email_address) {
      setTooltip(email_address);
      setLettersCount(2.5);
    } else if (stationId) {
      setInitials(stationId);
      setTooltip(stationName || "");
      setLettersCount(3);
    }
  }, [first_name, last_name, stationId, email_address, stationName]);

  // Effect to generate and set a random color on mount
  useEffect(() => {
    setColor(generateColor({ brightness: 40, saturation: 80 }));
  }, []);

  /**
   * Generate an avatar image as a base64 PNG data URL with text initials.
   *
   * @param {string} text - Text to render inside the avatar.
   * @param {number} width - Width of the canvas.
   * @param {number} height - Height of the canvas.
   * @param {string} bgColor - Background color in HEX.
   * @param {string} [textColor="#fff"] - Color of the text.
   * @returns {string} Base64 data URL representing the generated avatar image.
   */
  function generateAvatarImage(
    text,
    width,
    height,
    bgColor = "#3498db",
    textColor = "#fff"
  ) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // Draw circle background
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "#3ea1ff"; // e.g., '#ffffff'
    ctx.lineWidth = 2; // width of the border
    ctx.stroke();

    // Draw initials
    ctx.fillStyle = textColor;
    ctx.font = `${(width - textPadding) / lettersCount}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, width / 2, height / 2);

    return canvas.toDataURL("image/png");
  }

  return (
    <img
      src={generateAvatarImage(initials, width, height, color)}
      width={width}
      height={height}
      style={{ borderRadius: "50%" }}
      alt="Avatar"
      title={tooltip}
    />
  );
};

export default AvatarPicture;

